import { useStoreLiveCa, useStoreSupplyTimeline } from '@gozoki/api'
import { frenchFormat } from '@gozoki/tools'
import { Skeleton } from '@gozoki/ui/components/ui/skeleton'
import { DateTime } from 'luxon'
import heroWave from '~/assets/dashboard/hero-wave.svg'
import { TradeUpIcon } from './icons/dashboard-icons'
import { CardDivider, CardTitle, DashboardCard, TrendBadge } from './ui/DashboardCard'

/**
 * Carte hero verte (Figma 10715:55866) : activité du jour de la boutique connectée — CA temps
 * réel + tendance vs S-1 (`/store/live-ca`), unités vendues et avancement sur la prévision de
 * ventes du jour (`/store/supply/timeline`).
 *
 * La timeline est demandée avec les MÊMES paramètres que `FluxSupplyCard` : même clé React Query,
 * donc une seule requête pour les deux cartes. Ses `kpis` portent déjà sur la seule journée en
 * cours (cf. `computeKpis` côté back), la fenêtre de 7 jours ne concerne que le graphe.
 */
export function CaLiveHeroCard() {
  const { data: liveCa, isLoading } = useStoreLiveCa()
  const { data: supply, isLoading: isSupplyLoading } = useStoreSupplyTimeline({ days: 7 })

  const now = DateTime.now().setZone('Europe/Paris').setLocale('fr')
  const stamp = `${now.toFormat('ccc').replace('.', '').toUpperCase()}. ${now.toFormat('HH:mm')}`

  // Unités vendues depuis l'ouverture vs prévision de ventes de la journée entière.
  const soldUnits = supply ? Math.round(supply.kpis.sales) : null
  const forecastUnits = supply ? Math.round(supply.kpis.vpj) : null
  const forecastProgress =
    soldUnits !== null && forecastUnits ? Math.min(100, (soldUnits / forecastUnits) * 100) : 0

  return (
    <DashboardCard
      className="relative h-[234px] shrink-0 border-[#0000000F] text-[#CEE3C4]"
      style={{
        backgroundImage:
          'linear-gradient(115.37deg, #354A31 13.114%, #507247 61.367%), linear-gradient(90deg, #B8D9A8 0%, #B8D9A8 100%)',
      }}
    >
      {/* Vague décorative de la maquette, posée sous le contenu. */}
      <img
        src={heroWave}
        alt=""
        aria-hidden
        className="pointer-events-none absolute top-[-70px] left-[10px] w-[525px] max-w-none -rotate-120"
      />

      <div className="relative">
        <CardTitle
          icon={<TradeUpIcon />}
          iconBoxClassName="bg-[#4F6A47] text-[#CEE3C4] size-[40px]"
          title="C.A"
          titleClassName="text-[#CEE3C4]"
          meta={stamp}
          metaClassName="text-[#CEE3C4]"
        />
      </div>

      <CardDivider className="relative bg-[#FFFFFF33]" />

      <div className="relative flex items-start justify-between gap-[12px]">
        <div className="flex min-w-0 flex-col text-[#F9F8F1]">
          {isLoading ? (
            <Skeleton className="h-[44px] w-[160px] bg-white/30" />
          ) : (
            <span className="font-brand text-[40px] leading-[44px] font-extrabold whitespace-nowrap">
              {liveCa ? `${frenchFormat(Math.round(liveCa.caToday))} €` : '—'}
            </span>
          )}
          {/* Pas de source UVC côté API pour l'instant. */}
          {isSupplyLoading ? (
            <Skeleton className="mt-[4px] h-[22px] w-[110px] bg-white/30" />
          ) : (
            <span className="font-brand text-[22px] leading-[26px] font-extrabold">
              {soldUnits === null ? '—' : frenchFormat(soldUnits)} UVC
            </span>
          )}
        </div>
        <TrendBadge value={liveCa?.vsPrevWeekPercent ?? null} className="text-[#F9F8F1]" />
      </div>

      <div className="relative flex flex-col gap-[3px]">
        <div className="flex items-center gap-[10px]">
          <span className="min-w-0 flex-1 font-sans text-[14px] leading-[16px] font-medium text-[#F9F8F1]">
            Prévisions / h
          </span>
          {/* Réalisé / attendu : aucune prévision horaire exposée par l'API boutique. */}
          <span className="font-sans text-[16px] font-semibold whitespace-nowrap uppercase">
            <span className="text-[#F9F8F1]">
              {soldUnits === null ? '—' : frenchFormat(soldUnits)} u. /
            </span>{' '}
            <span className="text-[#86AB7D]">
              {forecastUnits === null ? '—' : frenchFormat(forecastUnits)} u.
            </span>
          </span>
        </div>
        <div className="h-[8px] w-full overflow-hidden rounded-[10px] bg-[#CEE3C4]">
          <div
            className="h-full rounded-[10px] bg-[#A4C494]"
            style={{ width: `${forecastProgress}%` }}
          />
        </div>
      </div>
    </DashboardCard>
  )
}
